import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { CalculationSetupApis } from 'apis/CalculationSetupApis';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import EditRewardIcon from 'components/Icons/EditRewardIcon';
import ImportIcon from 'components/Icons/ImportIcon';
import { UploadModal } from 'components/UploadModal_V2';
import { useUser } from 'contexts/UserProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { toastError, toastSuccess } from 'utils/constants/functions';
import {
  IMPORT_EDIT_FRAME,
  IMPORT_REWARD_FRAME,
} from 'utils/constants/importId';
import FilterRewardFrame from './FilterRewardFrame';
import TableRewardFrameTab from './TableRewardFrameTab';

const IMPORT_EDIT = 1;
const IMPORT_CREATE = 2;
const NO_ACTION = 0;

const RewardFrameTab = () => {
  const { user } = useUser();
  const childRef = useRef<any>(null);
  const [formTable] = useForm();
  const [isAdd, setIsAdd] = useState<boolean>(false);
  const [dataTable, setDataTable] = useState<any>({});
  const [indexHandle, setIndexHandle] = useState<number>(-1);
  const [isOpenImportModal, setIsOpenImportModal] = useState<boolean>(false);
  const [importType, setImportType] = useState<number>(NO_ACTION);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [filters, setFilters] = useState<any>({});

  const mutateGetRewardFrame = useMutation({
    mutationFn: CalculationSetupApis.getListRewardFrame,
    onSuccess: (res, variables) => {
      const result = res?.data?.getRewardSeqment || {};
      if (variables.type === 'export') {
        exportExcel_v2(variables.headerRow, result.items || [], 'Khung thưởng');
      } else {
        setDataTable(result);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const {
    mutate: mutateUpdateRewardFrame,
    isPending: isPendingUpdateRewardFrame,
  } = useMutation({
    mutationFn: CalculationSetupApis.updateRewardSeqmentById,
    onSuccess: (res) => {
      if (res.errors?.length > 0) {
        toastError(res.errors[0].message);
        return;
      }

      toastSuccess('Lưu thành công');
      handleCancel();
      refetchGetRewardFrame();
    },
  });

  const refetchGetRewardFrame = useCallback(
    async (
      page = 1,
      page_size = DEFAULT_PAGE_SIZE,
      isExport?: boolean,
      headerRow?: any,
    ) => {
      try {
        await mutateGetRewardFrame.mutateAsync({
          page: isExport ? 1 : page,
          page_size: isExport
            ? dataTable.pageInfo?.totalItems || 1000
            : page_size,
          ...(isExport && {
            headerRow,
            type: 'export',
          }),
          ...filters,
        });
      } catch (error) {
        console.log('mutateGetRewardFrame', error);
      }
    },
    [dataTable.pageInfo?.totalItems, filters],
  );

  useEffect(() => {
    // console.log('user', user);
    refetchGetRewardFrame();
  }, [refetchGetRewardFrame]);

  const mutateSaveRewardFrame = useMutation({
    mutationFn: CalculationSetupApis.saveRewardFrame,
    onSuccess: (res) => {
      toastSuccess('Lưu thành công');
      refetchGetRewardFrame();
    },
    onError: (error) => {
      toastError('Lưu thất bại');
      console.log(error);
    },
  });

  const handleExportExcel = () => {
    if (childRef.current) {
      childRef.current.exportExcel();
    }
  };

  const handleCancel = () => {
    setIsAdd(false);
    setIndexHandle(-1);
    formTable.resetFields();
  };

  const handleAddRow = () => {
    setIsAdd(true);
    setIndexHandle(0);
    setDataTable((pre: any) => ({
      ...pre,
      items: [{ id: Date.now() }].concat(pre.items),
    }));
  };

  const handleCancelTable = () => {
    if (isAdd) {
      setDataTable((pre: any) => ({
        ...pre,
        items: (pre?.items || []).slice(1),
      }));
    }
    handleCancel();
  };

  const handleSaveTable = () => {
    const data = formTable.getFieldsValue();
    const row = dataTable.items[indexHandle];

    mutateUpdateRewardFrame({
      ...data,
      id: row.id,
    });
  };

  const handleEditRow = (index: number) => {
    setIndexHandle(index);
    setIsAdd(false);
    const row = dataTable.items[index];
    formTable.setFieldsValue(row);
  };

  const handleImport = (type: number) => {
    // console.log('import');
    setIsOpenImportModal(true);
    setImportType(type);
  };

  const handleFilter = (values: any) => {
    setFilters(values);
  };

  const handleCancelFilter = () => {
    setFilters({});
  };

  useEffect(() => {
    refetchGetRewardFrame();
  }, [filters, refetchGetRewardFrame]);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'} className="mb-4">
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
        <Col>
          <ButtonExport
            onClick={handleExportExcel}
            disabled={isAdd || indexHandle !== -1}
          />
        </Col>
        <Col>
          <Button
            title="Import Thêm mới"
            iconBefore={<ImportIcon />}
            disabled={
              isAdd ||
              indexHandle !== -1 ||
              !(user?.isAdmin || user?.isHO || user?.isNS)
            }
            handleOnclick={() => handleImport(IMPORT_CREATE)}
          />
        </Col>
        <Col>
          <Button
            title="Import Chỉnh sửa"
            iconBefore={<EditRewardIcon />}
            disabled={
              isAdd ||
              indexHandle !== -1 ||
              !(user?.isAdmin || user?.isHO || user?.isNS)
            }
            handleOnclick={() => handleImport(IMPORT_EDIT)}
          />
        </Col>
      </Row>
      <FilterRewardFrame
        isOpen={isOpenFilter}
        onFinish={handleFilter}
        onCancel={handleCancelFilter}
      />
      <TableRewardFrameTab
        ref={childRef}
        form={formTable}
        data={dataTable}
        isAdd={isAdd}
        // setIsAdd={setIsAdd}
        indexHandle={indexHandle}
        // setIndexHandle={setIndexHandle}
        onCancel={handleCancelTable}
        onEdit={handleEditRow}
        onSave={handleSaveTable}
        loading={mutateGetRewardFrame.isPending || isPendingUpdateRewardFrame}
        refetch={refetchGetRewardFrame}
      />
      <UploadModal
        importId={
          importType === IMPORT_CREATE ? IMPORT_REWARD_FRAME : IMPORT_EDIT_FRAME
        }
        templateFileName="Khung thưởng"
        open={isOpenImportModal}
        title={
          importType === IMPORT_CREATE
            ? 'Thêm mới khung thưởng'
            : 'Chỉnh sửa khung thưởng'
        }
        setOpen={setIsOpenImportModal}
        refetch={refetchGetRewardFrame}
      />
    </>
  );
};

export default RewardFrameTab;
